export enum BbchMajorStage {
  Germination = 0,
  LeafDevelopment = 1,
  HarvestableVegetativeParts = 4,
  InflorescenceEmergence = 5,
  Flowering = 6,
  FruitDevelopment = 7,
  RipeningOfFruitAndSeed = 8,
  Senescence = 9,
}

export interface BbchStage {
  readonly majorStage: BbchMajorStage;
  readonly code2Digit: string;
  readonly code3Digit: string;
  readonly descriptionKey: string;
}

/**
 * BBCH identification keys for bulb vegetables (onion, leek, garlic, shallot).
 * Based on the BBCH-scale (bulb vegetable) — Feller et al. (1995).
 *
 * Descriptions are translation keys under 'downyMildew.bbch.stage.<code3Digit>'.
 */
export const BBCH_BULB_VEGETABLE_STAGES: readonly BbchStage[] = [
  // 0: Germination
  { majorStage: BbchMajorStage.Germination, code2Digit: '00', code3Digit: '000', descriptionKey: 'downyMildew.bbch.stage.000' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '01', code3Digit: '001', descriptionKey: 'downyMildew.bbch.stage.001' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '03', code3Digit: '003', descriptionKey: 'downyMildew.bbch.stage.003' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '05', code3Digit: '005', descriptionKey: 'downyMildew.bbch.stage.005' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '07', code3Digit: '007', descriptionKey: 'downyMildew.bbch.stage.007' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '09', code3Digit: '009', descriptionKey: 'downyMildew.bbch.stage.009' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '', code3Digit: '010', descriptionKey: 'downyMildew.bbch.stage.010' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '', code3Digit: '011', descriptionKey: 'downyMildew.bbch.stage.011' },
  { majorStage: BbchMajorStage.Germination, code2Digit: '', code3Digit: '012', descriptionKey: 'downyMildew.bbch.stage.012' },

  // 1: Leaf development (Main shoot)
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '10', code3Digit: '100', descriptionKey: 'downyMildew.bbch.stage.100' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '11', code3Digit: '101', descriptionKey: 'downyMildew.bbch.stage.101' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '12', code3Digit: '102', descriptionKey: 'downyMildew.bbch.stage.102' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '13', code3Digit: '103', descriptionKey: 'downyMildew.bbch.stage.103' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '14', code3Digit: '104', descriptionKey: 'downyMildew.bbch.stage.104' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '15', code3Digit: '105', descriptionKey: 'downyMildew.bbch.stage.105' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '16', code3Digit: '106', descriptionKey: 'downyMildew.bbch.stage.106' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '17', code3Digit: '107', descriptionKey: 'downyMildew.bbch.stage.107' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '18', code3Digit: '108', descriptionKey: 'downyMildew.bbch.stage.108' },
  { majorStage: BbchMajorStage.LeafDevelopment, code2Digit: '19', code3Digit: '109', descriptionKey: 'downyMildew.bbch.stage.109' },

  // 4: Development of harvestable vegetative plant parts
  { majorStage: BbchMajorStage.HarvestableVegetativeParts, code2Digit: '41', code3Digit: '401', descriptionKey: 'downyMildew.bbch.stage.401' },
  { majorStage: BbchMajorStage.HarvestableVegetativeParts, code2Digit: '43', code3Digit: '403', descriptionKey: 'downyMildew.bbch.stage.403' },
  { majorStage: BbchMajorStage.HarvestableVegetativeParts, code2Digit: '45', code3Digit: '405', descriptionKey: 'downyMildew.bbch.stage.405' },
  { majorStage: BbchMajorStage.HarvestableVegetativeParts, code2Digit: '47', code3Digit: '407', descriptionKey: 'downyMildew.bbch.stage.407' },
  { majorStage: BbchMajorStage.HarvestableVegetativeParts, code2Digit: '48', code3Digit: '408', descriptionKey: 'downyMildew.bbch.stage.408' },
  { majorStage: BbchMajorStage.HarvestableVegetativeParts, code2Digit: '49', code3Digit: '409', descriptionKey: 'downyMildew.bbch.stage.409' },

  // 5: Inflorescence emergence
  { majorStage: BbchMajorStage.InflorescenceEmergence, code2Digit: '51', code3Digit: '501', descriptionKey: 'downyMildew.bbch.stage.501' },
  { majorStage: BbchMajorStage.InflorescenceEmergence, code2Digit: '53', code3Digit: '503', descriptionKey: 'downyMildew.bbch.stage.503' },
  { majorStage: BbchMajorStage.InflorescenceEmergence, code2Digit: '55', code3Digit: '505', descriptionKey: 'downyMildew.bbch.stage.505' },
  { majorStage: BbchMajorStage.InflorescenceEmergence, code2Digit: '57', code3Digit: '507', descriptionKey: 'downyMildew.bbch.stage.507' },
  { majorStage: BbchMajorStage.InflorescenceEmergence, code2Digit: '59', code3Digit: '509', descriptionKey: 'downyMildew.bbch.stage.509' },

  // 6: Flowering
  { majorStage: BbchMajorStage.Flowering, code2Digit: '60', code3Digit: '600', descriptionKey: 'downyMildew.bbch.stage.600' },
  { majorStage: BbchMajorStage.Flowering, code2Digit: '61', code3Digit: '601', descriptionKey: 'downyMildew.bbch.stage.601' },
  { majorStage: BbchMajorStage.Flowering, code2Digit: '62', code3Digit: '602', descriptionKey: 'downyMildew.bbch.stage.602' },
  { majorStage: BbchMajorStage.Flowering, code2Digit: '63', code3Digit: '603', descriptionKey: 'downyMildew.bbch.stage.603' },
  { majorStage: BbchMajorStage.Flowering, code2Digit: '64', code3Digit: '604', descriptionKey: 'downyMildew.bbch.stage.604' },
  { majorStage: BbchMajorStage.Flowering, code2Digit: '65', code3Digit: '605', descriptionKey: 'downyMildew.bbch.stage.605' },
  { majorStage: BbchMajorStage.Flowering, code2Digit: '67', code3Digit: '607', descriptionKey: 'downyMildew.bbch.stage.607' },
  { majorStage: BbchMajorStage.Flowering, code2Digit: '69', code3Digit: '609', descriptionKey: 'downyMildew.bbch.stage.609' },

  // 7: Development of fruit
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '71', code3Digit: '701', descriptionKey: 'downyMildew.bbch.stage.701' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '72', code3Digit: '702', descriptionKey: 'downyMildew.bbch.stage.702' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '73', code3Digit: '703', descriptionKey: 'downyMildew.bbch.stage.703' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '74', code3Digit: '704', descriptionKey: 'downyMildew.bbch.stage.704' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '75', code3Digit: '705', descriptionKey: 'downyMildew.bbch.stage.705' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '76', code3Digit: '706', descriptionKey: 'downyMildew.bbch.stage.706' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '77', code3Digit: '707', descriptionKey: 'downyMildew.bbch.stage.707' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '78', code3Digit: '708', descriptionKey: 'downyMildew.bbch.stage.708' },
  { majorStage: BbchMajorStage.FruitDevelopment, code2Digit: '79', code3Digit: '709', descriptionKey: 'downyMildew.bbch.stage.709' },

  // 8: Ripening of fruit and seed
  { majorStage: BbchMajorStage.RipeningOfFruitAndSeed, code2Digit: '80', code3Digit: '800', descriptionKey: 'downyMildew.bbch.stage.800' },
  { majorStage: BbchMajorStage.RipeningOfFruitAndSeed, code2Digit: '81', code3Digit: '801', descriptionKey: 'downyMildew.bbch.stage.801' },
  { majorStage: BbchMajorStage.RipeningOfFruitAndSeed, code2Digit: '83', code3Digit: '803', descriptionKey: 'downyMildew.bbch.stage.803' },
  { majorStage: BbchMajorStage.RipeningOfFruitAndSeed, code2Digit: '85', code3Digit: '805', descriptionKey: 'downyMildew.bbch.stage.805' },
  { majorStage: BbchMajorStage.RipeningOfFruitAndSeed, code2Digit: '87', code3Digit: '807', descriptionKey: 'downyMildew.bbch.stage.807' },
  { majorStage: BbchMajorStage.RipeningOfFruitAndSeed, code2Digit: '89', code3Digit: '809', descriptionKey: 'downyMildew.bbch.stage.809' },

  // 9: Senescence
  { majorStage: BbchMajorStage.Senescence, code2Digit: '90', code3Digit: '900', descriptionKey: 'downyMildew.bbch.stage.900' },
  { majorStage: BbchMajorStage.Senescence, code2Digit: '91', code3Digit: '901', descriptionKey: 'downyMildew.bbch.stage.901' },
  { majorStage: BbchMajorStage.Senescence, code2Digit: '97', code3Digit: '907', descriptionKey: 'downyMildew.bbch.stage.907' },
  { majorStage: BbchMajorStage.Senescence, code2Digit: '99', code3Digit: '909', descriptionKey: 'downyMildew.bbch.stage.909' },
] as const;

/**
 * Major stages currently enabled for selection.
 * Stages 5–7 remain disabled for the current version.
 */
const ENABLED_MAJOR_STAGES = new Set<BbchMajorStage>([
  BbchMajorStage.Germination,
  BbchMajorStage.LeafDevelopment,
  BbchMajorStage.HarvestableVegetativeParts,
  // BbchMajorStage.InflorescenceEmergence,   // disabled
  // BbchMajorStage.Flowering,                // disabled
  // BbchMajorStage.FruitDevelopment,         // disabled
  BbchMajorStage.RipeningOfFruitAndSeed,
  BbchMajorStage.Senescence,
]);

/**
 * Returns the ordered list of distinct major stages used in the bulb vegetable scale.
 */
export function getMajorStages(): BbchMajorStage[] {
  return [
    BbchMajorStage.Germination,
    BbchMajorStage.LeafDevelopment,
    BbchMajorStage.HarvestableVegetativeParts,
    BbchMajorStage.InflorescenceEmergence,
    BbchMajorStage.Flowering,
    BbchMajorStage.FruitDevelopment,
    BbchMajorStage.RipeningOfFruitAndSeed,
    BbchMajorStage.Senescence,
  ];
}

/**
 * Returns whether a major stage is currently enabled for selection.
 */
export function isMajorStageEnabled(majorStage: BbchMajorStage): boolean {
  return ENABLED_MAJOR_STAGES.has(majorStage);
}

/**
 * Returns all BBCH stages for the given major stage.
 */
export function getStagesByMajorStage(majorStage: BbchMajorStage): BbchStage[] {
  return BBCH_BULB_VEGETABLE_STAGES.filter((s) => s.majorStage === majorStage);
}

/**
 * Finds a BBCH stage by its 3-digit code.
 */
export function findByCode3Digit(code: string): BbchStage | undefined {
  return BBCH_BULB_VEGETABLE_STAGES.find((s) => s.code3Digit === code);
}

/**
 * Returns the translation key for a major stage name.
 */
export function getMajorStageDescriptionKey(majorStage: BbchMajorStage): string {
  return `downyMildew.bbch.majorStage.${majorStage}`;
}

/**
 * Formats BBCH code for UI display.
 *
 * Internal/API codes stay 3-digit (e.g. 101, 401), but display should use
 * the 2-digit representation for major stages 1-9 when the middle digit is 0
 * (e.g. 101 -> 11, 401 -> 41). Codes in major stage 0 keep 3 digits.
 */
export function formatBbchDisplayCode(code3Digit: string): string {
  if (/^[1-9]0\d$/.test(code3Digit)) {
    return `${code3Digit[0]}${code3Digit[2]}`;
  }

  return code3Digit;
}
